import React from 'react';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { getPaletteEntry } from '../../../statusPalette';

// v0.4 Task 6: execution telemetry footer shared by PeerNodeRich +
// TurnMarkerNodeRich (asst). Lives below whatever identity chrome
// the parent node renders. Error state replaces prompt/response
// previews with a palette-toned error block.

export interface ExecutionSectionProps {
  model?: string | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  costUsd?: number | null;
  durationMs?: number | null;
  prompt?: string | null;
  response?: string | null;
  error?: string | null;
  status?: string | null;
  execSectionId?: string;
}

function fmtCost(usd?: number | null): string | null {
  if (usd == null) return null;
  if (usd === 0) return '$0';
  if (usd < 0.0001) return `$${usd.toFixed(6)}`;
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}
function fmtDuration(ms?: number | null): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}
function shortenModel(modelId?: string | null): string | null {
  if (!modelId) return null;
  const tail = modelId.split('/').pop() ?? modelId;
  return tail.replace(/-\d{8}$/, '');
}

const Preview: React.FC<{ text: string; maxLines?: number }> = ({
  text,
  maxLines = 4,
}) => (
  <div
    className="whitespace-pre-wrap break-words"
    style={{
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}
  >
    {text}
  </div>
);

export const ExecutionSection: React.FC<ExecutionSectionProps> = ({
  model,
  promptTokens,
  completionTokens,
  totalTokens,
  costUsd,
  durationMs,
  prompt,
  response,
  error,
  status,
  execSectionId,
}) => {
  const { darkMode } = useTheme();
  const palette = getPaletteEntry(status);
  const isError = status === 'error' || !!error;
  const shortModel = shortenModel(model);
  const cost = fmtCost(costUsd);
  const duration = fmtDuration(durationMs);
  const hasStats = totalTokens != null || cost != null || duration != null;
  const hasBody = shortModel || hasStats || isError || prompt || response;
  if (!hasBody) return null;

  return (
    <div
      id={execSectionId}
      className={`p-3 border-t ${
        darkMode ? 'border-gray-700' : 'border-gray-200'
      } text-[11px]`}
    >
      {shortModel && (
        <div
          className={`font-mono ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          } mb-1`}
        >
          {shortModel}
        </div>
      )}
      {hasStats && (
        <div
          className={`flex items-center gap-3 font-mono mb-2 ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {totalTokens != null && (
            <span title={`in ${promptTokens ?? 0} · out ${completionTokens ?? 0}`}>
              {totalTokens.toLocaleString()} tok
            </span>
          )}
          {cost && <span>💰 {cost}</span>}
          {duration && <span>⏱ {duration}</span>}
        </div>
      )}
      {isError && (
        <div
          className={`mb-2 p-2 rounded border ${palette.bgClass} ${palette.color}`}
        >
          <div className="font-semibold text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1">
            <span>{palette.icon}</span> Error
          </div>
          <div>{error ?? 'Execution failed'}</div>
        </div>
      )}
      {prompt && !isError && (
        <div className="mb-2">
          <div
            className={`text-[9px] uppercase tracking-widest mb-0.5 ${
              darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            Prompt
          </div>
          <Preview text={prompt} maxLines={3} />
        </div>
      )}
      {response && !isError && (
        <div>
          <div
            className={`text-[9px] uppercase tracking-widest mb-0.5 ${
              darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            Response
          </div>
          <Preview text={response} maxLines={6} />
        </div>
      )}
    </div>
  );
};
